import { createFileRoute } from "@tanstack/react-router";
import SparklesIcon from "lucide-react/dist/esm/icons/sparkles";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { PageHeader } from "@/components/shell/page-frame";
import { EmptyState } from "@/components/states/state-views";
import { StatusBadge, StatusDot } from "@/components/status/status-badge";
import { SEVERITY_LABEL, SEVERITY_TONE } from "@/components/status/tone";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { Conversation } from "@/features/inbox/types";
import { useInbox } from "@/features/inbox/use-inbox";
import { formatDateTime, formatElapsed, formatRelative } from "@/lib/format";
import { useNow } from "@/lib/use-now";
import { cn } from "@/lib/utils";

const inboxSearchSchema = z.object({
  urgency: z.enum(["all", "critical", "warning", "info"]).catch("all").default("all"),
  selected: z.string().optional()
});

export const Route = createFileRoute("/inbox")({
  validateSearch: inboxSearchSchema,
  component: InboxPage
});

/**
 * 会話のAI監視（4-2）。未返信を放置時間順に並べ、AIが要約と返信ドラフトを用意し、
 * 人が確認して送信する。送信の最終決定は必ず人に残す（原則 8 の対応導線 + human-in-the-loop）。
 */
function InboxPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const now = useNow();
  const { conversations, openCount, sendReply, escalate } = useInbox();

  const filtered = useMemo(
    () =>
      conversations.filter(
        (conversation) => search.urgency === "all" || conversation.urgency === search.urgency
      ),
    [conversations, search.urgency]
  );

  const selectedId = search.selected ?? filtered[0]?.id;
  const selected = conversations.find((conversation) => conversation.id === selectedId);

  const select = (id: string) => {
    void navigate({ search: (previous) => ({ ...previous, selected: id }) });
  };

  return (
    <>
      <PageHeader
        kind="operations"
        title="インボックス"
        description="未返信を放置時間順に。AIが要約と返信案を用意し、送信は人が判断する"
        controls={
          <div className="flex items-center gap-1" role="tablist" aria-label="緊急度で絞り込み">
            {(["all", "critical", "warning", "info"] as const).map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={search.urgency === value}
                onClick={() =>
                  navigate({ search: (previous) => ({ ...previous, urgency: value }) })
                }
                className={cn(
                  "inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-xs transition-colors",
                  search.urgency === value
                    ? "border-brand-line bg-brand-subtle text-fg"
                    : "border-transparent text-fg-muted hover:bg-surface-hover"
                )}
              >
                {value === "all" ? "すべて" : SEVERITY_LABEL[value]}
              </button>
            ))}
          </div>
        }
      />

      <div className="flex min-h-0 flex-1">
        {/* 一覧: 放置時間順。左に固定して走査する（F パターン） */}
        <div className="flex w-full max-w-96 shrink-0 flex-col border-r border-line">
          <div className="flex h-9 items-center justify-between border-b border-line px-4 text-2xs text-fg-subtle">
            <span className="eyebrow">未返信 {openCount}件</span>
            <span>放置時間順</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filtered.length > 0 ? (
              <ul>
                {filtered.map((conversation) => (
                  <li key={conversation.id}>
                    <ConversationRow
                      conversation={conversation}
                      now={now}
                      active={conversation.id === selectedId}
                      onSelect={() => select(conversation.id)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                title="該当する会話はありません"
                description="緊急度の絞り込みを変えるか、すべてを表示してください。"
              />
            )}
          </div>
        </div>

        {/* 詳細: スレッド + AI要約 + 返信ドラフト */}
        <div className="min-w-0 flex-1">
          {selected ? (
            <ConversationDetail
              key={selected.id}
              conversation={selected}
              now={now}
              onSend={(body) => sendReply(selected.id, body)}
              onEscalate={() => escalate(selected.id)}
            />
          ) : (
            <EmptyState
              title="会話を選択してください"
              description="左の一覧から会話を選ぶと、AIの要約と返信案が表示されます。"
              className="h-full"
            />
          )}
        </div>
      </div>
    </>
  );
}

interface ConversationRowProps {
  conversation: Conversation;
  now: Date;
  active: boolean;
  onSelect: () => void;
}

function ConversationRow({ conversation, now, active, onSelect }: ConversationRowProps) {
  const lastMessage = conversation.thread[conversation.thread.length - 1];
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full flex-col gap-1 border-b border-line px-4 py-3 text-left transition-colors",
        active ? "bg-brand-subtle" : "hover:bg-surface-hover"
      )}
    >
      <div className="flex items-center gap-2">
        <StatusDot
          tone={SEVERITY_TONE[conversation.urgency]}
          pulse={conversation.urgency === "critical" && conversation.state === "open"}
        />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">
          {conversation.customerName}
        </span>
        {conversation.state === "replied" ? (
          <StatusBadge tone="success" glyph={false} className="h-4">
            返信済
          </StatusBadge>
        ) : conversation.state === "escalated" ? (
          <StatusBadge tone="warning" glyph={false} className="h-4">
            エスカレ
          </StatusBadge>
        ) : (
          <time
            dateTime={conversation.waitingSince}
            className="num shrink-0 text-2xs text-fg-subtle"
          >
            {formatElapsed(conversation.waitingSince, now)}
          </time>
        )}
      </div>
      <span className="truncate text-sm text-fg">{conversation.subject}</span>
      <span className="truncate text-xs text-fg-subtle">{lastMessage?.body}</span>
    </button>
  );
}

interface ConversationDetailProps {
  conversation: Conversation;
  now: Date;
  onSend: (body: string) => void;
  onEscalate: () => void;
}

function ConversationDetail({ conversation, now, onSend, onEscalate }: ConversationDetailProps) {
  const [draft, setDraft] = useState(conversation.aiDraft);
  const replied = conversation.state === "replied";

  // 別の会話に切り替わったらドラフトを差し替える
  useEffect(() => {
    setDraft(conversation.aiDraft);
  }, [conversation.aiDraft]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusBadge tone={SEVERITY_TONE[conversation.urgency]}>
              {SEVERITY_LABEL[conversation.urgency]}
            </StatusBadge>
            <h2 className="truncate text-sm font-semibold text-fg">{conversation.subject}</h2>
          </div>
          <p className="mt-0.5 truncate text-xs text-fg-subtle">
            {conversation.customerName} · {conversation.email} ·{" "}
            {conversation.shopDomain.replace(".myshopify.com", "")}
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onEscalate} disabled={replied}>
          担当へエスカレ
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
        {conversation.thread.map((message) => (
          <div
            key={message.id}
            className={cn(
              "max-w-[80%] rounded-lg border px-3 py-2 text-sm",
              message.from === "customer"
                ? "border-line bg-surface"
                : "ml-auto border-brand-line bg-brand-subtle"
            )}
          >
            <p className="mb-1 flex items-center gap-2 text-2xs text-fg-subtle">
              <span className="font-semibold text-fg-muted">
                {message.from === "customer" ? conversation.customerName : "運用チーム"}
              </span>
              <time dateTime={message.at}>{formatRelative(message.at, now)}</time>
            </p>
            <p className="whitespace-pre-wrap text-fg">{message.body}</p>
          </div>
        ))}

        {/* AI が判定した意図の要約 */}
        <div className="rounded-lg border border-info-line bg-info-subtle p-3">
          <p className="mb-1 flex items-center gap-1.5 text-2xs font-semibold text-info-fg">
            <SparklesIcon className="size-3" aria-hidden="true" />
            AIによる要約
          </p>
          <p className="text-sm text-fg">{conversation.aiSummary}</p>
        </div>
      </div>

      {/* 返信ドラフト: AI が下書き、人が編集して送信 */}
      <div className="border-t border-line p-4">
        {replied ? (
          <p className="flex items-center gap-2 py-2 text-sm text-success-fg">
            <StatusDot tone="success" />
            {formatDateTime(conversation.thread[conversation.thread.length - 1]?.at ?? now)}{" "}
            に返信済みです
          </p>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between">
              <label htmlFor="reply-draft" className="eyebrow">
                返信ドラフト（AI生成・送信前に確認）
              </label>
              <Button
                type="button"
                size="xs"
                variant="ghost"
                onClick={() => setDraft(conversation.aiDraft)}
              >
                AI案に戻す
              </Button>
            </div>
            <Textarea
              id="reply-draft"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={5}
              className="mb-2 resize-none"
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                onClick={() => onSend(draft)}
                disabled={draft.trim().length === 0}
              >
                確認して送信
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
